/*
 * Naturlabs PDP v3 (sections nl-v3-pdp-*). Vanilla, no dependencies, loaded with defer.
 *
 * - [data-v3-submit]: any button outside the main buy box (anchor bar, owner card, bottom bar) submits the main
 *   product form of the page (selected variant and quantity), so the cart drawer and errors behave like the main CTA.
 * - <v3-buybar>: bottom buy bar below 1024 px. Visible whenever the main add to cart button is off screen (above or
 *   below), hidden over the footer and while the cookie consent banner is open.
 * - <v3-anchors>: removes links whose section is missing, numbers the remaining links and the section eyebrows in page
 *   order, marks the active link, shows the mini product with price and CTA once the main button scrolled away.
 * - <v3-video>: muted loop that plays only while on screen; never autoplays under prefers-reduced-motion or with the
 *   motion setting off (html.nl-motion-off); play / pause toggle for everybody.
 * - [data-v3-fixed]: the product of the page stays selected in the optional set.
 * Motion: transform and opacity only (CSS), nothing here animates layout.
 */
(() => {
  if (window.NLv3Pdp) return;
  window.NLv3Pdp = true;

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  const reduced = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.documentElement.classList.contains('nl-motion-off');

  const mainForm = () => document.querySelector('[data-v3-main] form[id^="ProductForm-"]');
  const mainAtc = () => document.querySelector('[data-v3-main] [data-v3-main-atc]');

  /* ---------- Submit the main form from secondary CTAs ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-v3-submit]');
    if (!button || button.disabled) return;
    const form = mainForm();
    if (!form) return;
    event.preventDefault();
    const atc = mainAtc();
    if (atc && atc.disabled) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit(atc || undefined);
    else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  });

  /* ---------- Optional set: this product stays selected ---------- */
  document.addEventListener(
    'change',
    (event) => {
      const box = event.target.closest('[data-v3-fixed]');
      if (box && !box.checked) {
        box.checked = true;
      }
    },
    true
  );

  /* ---------- Cookie consent banner (keeps the bottom bar out of its way) ---------- */
  const CONSENT = '#shopify-pc__banner, .shopify-pc__banner__dialog';
  const consentOpen = () => {
    const banner = document.querySelector(CONSENT);
    if (!banner) return false;
    const style = getComputedStyle(banner);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    const rect = banner.getBoundingClientRect();
    return rect.height > 0 && rect.width > 0 && rect.top < window.innerHeight;
  };
  const consentListeners = new Set();
  let consentState = null;
  const syncConsent = () => {
    const open = consentOpen();
    if (open === consentState) return;
    consentState = open;
    document.documentElement.toggleAttribute('data-v3-consent-open', open);
    consentListeners.forEach((fn) => fn(open));
  };
  let consentWatching = false;
  const watchConsent = () => {
    if (consentWatching) return;
    consentWatching = true;
    syncConsent();
    let checks = 0;
    const timer = window.setInterval(() => {
      syncConsent();
      checks += 1;
      if (checks >= 20) window.clearInterval(timer);
    }, 750);
    if ('MutationObserver' in window) {
      let frame = 0;
      new MutationObserver(() => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          syncConsent();
        });
      }).observe(document.body, { childList: true, subtree: false, attributes: true, attributeFilter: ['class', 'style'] });
    }
    document.addEventListener('click', () => window.setTimeout(syncConsent, 300), true);
  };

  /* ---------- <v3-buybar> ---------- */
  class V3Buybar extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.target = this.dataset.target ? document.querySelector(this.dataset.target) : null;
      this.panel = this.firstElementChild;
      if (!this.target || !this.panel || !('IntersectionObserver' in window)) return;
      this._bound = true;
      this.offscreen = false;
      this.overFooter = false;
      this.consent = false;
      this.panel.inert = true;

      this.io = new IntersectionObserver(([entry]) => {
        this.offscreen = !entry.isIntersecting;
        this.update();
      });
      this.io.observe(this.target);

      const footer = document.querySelector('.nl-footer, footer');
      if (footer) {
        this.fo = new IntersectionObserver(([entry]) => {
          this.overFooter = entry.isIntersecting;
          this.update();
        });
        this.fo.observe(footer);
      }
      this.onConsent = (open) => {
        this.consent = open;
        this.update();
      };
      consentListeners.add(this.onConsent);
      watchConsent();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.fo) this.fo.disconnect();
      consentListeners.delete(this.onConsent);
      this._bound = false;
    }

    update() {
      const visible = this.offscreen && !this.overFooter && !this.consent;
      this.toggleAttribute('data-visible', visible);
      this.panel.inert = !visible;
      document.documentElement.toggleAttribute('data-v3-buybar', visible);
    }
  }
  define('v3-buybar', V3Buybar);

  /* ---------- Section numbering shared by the anchor bar and the section eyebrows ---------- */
  const ORDER = ['v3-na-co', 'v3-pro-koho', 'v3-proc', 'v3-slozeni', 'v3-kvalita', 'v3-uzivani', 'v3-otazky'];
  const pad = (n) => (n < 10 ? `0${n}` : String(n));
  const numberSections = () => {
    const present = ORDER.map((id) => document.getElementById(id)).filter(Boolean);
    present.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    const map = new Map();
    present.forEach((section, i) => {
      map.set(section.id, pad(i + 1));
      const idx = section.querySelector('.nl-v3-head .nl-v3-eyebrow__idx');
      if (idx) idx.textContent = pad(i + 1);
    });
    return map;
  };

  /* ---------- <v3-anchors> ---------- */
  class V3Anchors extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const numbers = numberSections();
      this.links = Array.from(this.querySelectorAll('[data-v3-anchor-link]')).filter((link) => {
        const id = (link.getAttribute('href') || '').slice(1);
        if (!numbers.has(id)) {
          const li = link.closest('li');
          (li || link).remove();
          return false;
        }
        const n = link.querySelector('[data-v3-anchor-n]');
        if (n) n.textContent = numbers.get(id);
        return true;
      });
      if (!this.links.length) {
        this.hidden = true;
        return;
      }
      this.links.sort((a, b) => Number(numbers.get(a.getAttribute('href').slice(1))) - Number(numbers.get(b.getAttribute('href').slice(1))));
      const list = this.querySelector('.nl-v3-anchors__list');
      if (list) {
        this.links.forEach((link) => {
          const li = link.closest('li');
          if (li && li.parentElement === list) list.appendChild(li);
        });
      }
      this.watchActive();
      this.watchBuy();
      this.onVariant = (event) => {
        const { variant } = event.detail || {};
        const price = this.querySelector('[data-v3-price]');
        if (!variant || !price) return;
        try {
          const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CZK';
          price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'cs-CZ', {
            style: 'currency',
            currency,
            minimumFractionDigits: variant.price % 100 ? 2 : 0,
            maximumFractionDigits: 2,
          }).format(variant.price / 100);
        } catch (error) {
          /* keep the server price */
        }
      };
      document.addEventListener('variant:change', this.onVariant);
    }

    disconnectedCallback() {
      if (this.activeIo) this.activeIo.disconnect();
      if (this.buyIo) this.buyIo.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('variant:change', this.onVariant);
      this._bound = false;
    }

    watchActive() {
      if (!('IntersectionObserver' in window)) return;
      const targets = this.links.map((link) => document.getElementById(link.getAttribute('href').slice(1)));
      const visible = new Map();
      this.activeIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => visible.set(entry.target.id, entry.isIntersecting));
          let current = null;
          for (const target of targets) {
            if (visible.get(target.id)) {
              current = target.id;
              break;
            }
          }
          this.links.forEach((link) => {
            const on = current && link.getAttribute('href') === `#${current}`;
            if (on) link.setAttribute('aria-current', 'true');
            else link.removeAttribute('aria-current');
            if (on) {
              const nav = link.closest('.nl-v3-anchors__nav');
              if (nav && nav.scrollWidth > nav.clientWidth) {
                const left = link.offsetLeft - (nav.clientWidth - link.offsetWidth) / 2;
                nav.scrollTo({ left, behavior: reduced() ? 'auto' : 'smooth' });
              }
            }
          });
        },
        { rootMargin: '-35% 0px -55% 0px' }
      );
      targets.forEach((target) => this.activeIo.observe(target));
    }

    watchBuy() {
      const atc = mainAtc();
      if (!atc || !('IntersectionObserver' in window)) {
        this.toggleAttribute('data-buy', true);
        return;
      }
      const cta = this.querySelector('[data-v3-anchors-cta]');
      let state = null;
      const apply = (past) => {
        if (past === state) return;
        state = past;
        this.toggleAttribute('data-buy', past);
        if (cta) cta.inert = !past;
      };
      this.buyIo = new IntersectionObserver(([entry]) => {
        apply(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.buyIo.observe(atc);
      /* A jump (Home key, anchor link, restored scroll) can move the button from above the viewport to below it
         without any intersection change, so the state is also read once per frame after scrolling. */
      let frame = 0;
      this.onScroll = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          apply(atc.getBoundingClientRect().bottom < 0);
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      apply(false);
    }
  }
  define('v3-anchors', V3Anchors);

  /* ---------- <v3-video> ---------- */
  class V3Video extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-v3-video-toggle]');
      if (!this.video) return;
      this._bound = true;
      this.userPaused = false;
      this.inView = false;
      this.video.muted = true;
      this.video.addEventListener('play', () => this.sync());
      this.video.addEventListener('pause', () => this.sync());
      if (this.toggle) {
        this.toggle.addEventListener('click', () => {
          if (this.video.paused) {
            this.userPaused = false;
            this.play();
          } else {
            this.userPaused = true;
            this.video.pause();
          }
        });
      }
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          ([entry]) => {
            this.inView = entry.isIntersecting;
            if (!this.inView) {
              if (!this.video.paused) this.video.pause();
              return;
            }
            if (!this.userPaused && !reduced()) this.play();
          },
          { threshold: 0.35 }
        );
        this.io.observe(this);
      }
      this.sync();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      this._bound = false;
    }

    play() {
      if (this.video.preload === 'none') this.video.preload = 'auto';
      const p = this.video.play();
      if (p && typeof p.catch === 'function') p.catch(() => this.sync());
    }

    sync() {
      const playing = !this.video.paused;
      this.toggleAttribute('data-playing', playing);
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-pressed', String(playing));
      this.toggle.setAttribute('aria-label', playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay);
    }
  }
  define('v3-video', V3Video);

  /* Section numbering also without the anchor bar */
  const ready = () => {
    if (!document.querySelector('v3-anchors')) numberSections();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();
})();
